import Camur from "@/utils/camurutils";
import { MANUALS_COLLECTION, DATASHEETS_COLLECTION } from "@/utils/manuals.js";

// Which documents the Documentation panel shows for a node or a controller, keyed on the
// node's capability. Same shape as MANUAL_GROUPS, except each document names the
// collection it comes from, since a node mixes a datasheet with a manual.
//
// The rows say only what kind of document they are. The group header already carries the
// node type's own name, taken from the language file, so naming the type again here would
// duplicate it and drift from it.
//
// Most node types have a datasheet but no manual of their own: only the P family, ZRA,
// CorroWatch and the FixVolt pair are covered by a manual, and each of those manuals
// covers several types at once. Entries listing only a datasheet are correct, not
// unfinished.
//
// The panel drops entries whose document has not synced, so a type can be listed here
// before its datasheet reaches storage.
//
// Read through a getter rather than stored: the language file arrives after this module is
// loaded, and the name has to follow the language being changed in App Settings.
const datasheet = ids => ({
  key: "datasheet",
  collection: DATASHEETS_COLLECTION,
  get labels() { return { en: window.app.$txt.datasheet }; },
  ids
});

const manual = ids => ({
  key: "manual",
  collection: MANUALS_COLLECTION,
  get labels() { return { en: window.app.$txt.manual }; },
  ids
});

// One document covers P, P4 and P+T, so the same entry is reused across those types.
const MANUAL_P_FAMILY = manual({
  en: "18EkYzbbsfAV_Zg0awp19Tc6ZdUm2pEpjq7gQ_MGzGHQ",
  de: "1OYYUDKcXIWIiWTnPxsFxB7JnNrQBBquTIkpUB2IwpfM"
});

// Covers Fixvolt 3 and Fixvolt 8. The smaller Fixvolt units have no manual.
const MANUAL_FIXVOLT = manual({
  en: "1F0IP84K8JPhtDeqG7XqXfV6US1UhuPrFKaX5g99_sG0",
  de: "1O_MjNY5KszL7lSKEsxjpxqNJ-fyGXhJN-euCJ-NqM14"
});

const MANUAL_ZRA = manual({
  en: "1AsWGtX66mLj9YiMLUAVGCt3jVQ3X6LRM2Wo04iV0LQg",
  de: "1_5AIbn6FaJTf5gkqMfI2tFYIXHPjOCNGjD3OmnlzfX0"
});

// English only, so a German reader gets the English document with the (EN) marker.
const MANUAL_CORROWATCH = manual({ en: "1Xt8ewt8r9C0AivittlFFK0eLL_iLQivpOl7Rbs8nuH4" });

const NODE_DOCUMENTS = {
  [Camur.node_type.ntype_lpr]: [
    datasheet({
      en: "1jDxmnZX1OQ_DImOCo-biA6RS9gjwKGiV5N7GixJdbzw",
      de: "1T6djpL3gK-v6ZQDtPIT1EPS-xSRvvTT69-43_wbsMbA"
    })
  ],

  [Camur.node_type.ntype_power_interface]: [
    datasheet({
      en: "1CfI8_ONV41LonOzXqWCdUUcxQ1vAlZkVV2cTvmKLm3s",
      de: "1p_ggyKrhUy9ZRdejtzogut3_ZjEn3wDwmN6cfevabts"
    })
  ],

  // a plain P node
  [Camur.node_type.ntype_pquick]: [
    datasheet({
      en: "14oZBG3kv371mdIZjG0skAJDcMUu1rXBlEzge_0xjXzA",
      de: "1VVOxcDM6_5fgKbmdAJtyoYG0qRNCTwCKpKTB4_BMA64"
    }),
    MANUAL_P_FAMILY
  ],

  [Camur.node_type.ntype_hum]: [
    datasheet({
      en: "1nNli9tGmJa-b0ehtsiLVzSRUz0KM8PY94r3dod8ecbg",
      de: "1nIt67AHaRzSDftkRShw-FSmjrtmAIVHjCeQGVIg8fZU"
    })
  ],

  [Camur.node_type.ntype_resmes]: [
    datasheet({
      en: "1x84L7C3V2QXaylzSEI5hz-PHtpz7rKIAO1oM8siOUXc",
      de: "1Ay49CpG7m2j-1iBHW2ijbdGez3OIxIAL6YRfEbNv7kI"
    })
  ],

  [Camur.node_type.ntype_zra]: [
    datasheet({
      en: "1HnWOAf0NQvFKZwJ84dDbCW5nTjQCVpJnuXdBMVohw1w",
      de: "1nbmJBNjBSoaTL9W2u6lKnmymbq9keupJg4kFj6q8Isc"
    }),
    MANUAL_ZRA
  ],

  [Camur.node_type.ntype_mre]: [
    datasheet({
      en: "1sHi_ldt8t_zUNVXC7Yxwc6jf-Wce5atg3sG12WKcqXM",
      de: "1jsj7sT3AVDoTZ9UYo_XdSEH91gFHEX42E7uCRRX3MMc"
    })
  ],

  [Camur.node_type.ntype_corrowatch]: [
    datasheet({
      en: "140J7YW6Zt5BL1lk4hojotwZcbuYPZVIMUvYlvScGUFg",
      de: "1JXh8dloFNZZaQwnr65hzmHWI96zTvjYNbIYS_XboiEQ"
    }),
    MANUAL_CORROWATCH
  ],

  [Camur.node_type.ntype_p4]: [
    datasheet({
      en: "1Vd4k-1Dz0ekx-Jxi142v_VYceqKCq3QnqXNp_2PaxQs",
      de: "1JkstQb62FnHqiET-ZgXan4jYP0X7lNNQRhFcE08Xv98"
    }),
    MANUAL_P_FAMILY
  ],

  [Camur.node_type.ntype_ladder]: [
    datasheet({
      en: "1Q9hobjz35jCHfH7-JewCUl-2s-VSlZT-oD4P8nzSHXA",
      de: "1t0VLa1JTJRHrFrRq85SGQuRyAcpkXwc7PmRNukJBjmU"
    })
  ],

  [Camur.node_type.ntype_pt]: [
    datasheet({
      en: "1tzSNk5NFJBL6DWv99ax81tMKAtdwLBrAS672nEuguJg",
      de: "1PlRv_t-9PVmmQot81evlyaiCgjn_MJyOubjjd4nlg-w"
    }),
    MANUAL_P_FAMILY
  ],

  // P4i and P4m are covered by the same manual; their datasheets do not exist yet, so add
  // a datasheet entry here once they do
  [Camur.node_type.ntype_p4i]: [MANUAL_P_FAMILY],

  [Camur.node_type.ntype_fv8]: [
    datasheet({
      en: "1CSMabEehnSN_7yIQMuKI4z4JoIyWeFHFwdeBCwI5lPE",
      de: "1Kd05mVBcsiCSSe9n7RvCS35KFH9GRJ6EWJgVJeTOxvY"
    }),
    MANUAL_FIXVOLT
  ],

  [Camur.node_type.ntype_fv3]: [
    datasheet({
      en: "17qBNUk4MrQJxOhaJcu0PdoythEqRdhMRFeBQTvyFY_E",
      de: "1F-YveXwMojHqsUHFrHh9NJjQjE7DW8shpTvREx9_tkQ"
    }),
    MANUAL_FIXVOLT
  ],

  [Camur.node_type.ntype_fv01]: [
    datasheet({
      en: "1Ps3LIhhL_EbmgmyrM97tTZdQaDn1WyoNlrKuH2WcDMw",
      de: "1YgG2S_S4UlNXvWeM8tcOeX6d0j95U0IKMeX3aQkn_84"
    })
  ],

  [Camur.node_type.ntype_fv1a]: [
    datasheet({
      en: "1B7JiRhCOTlSs-DkhwdLrdWbWaNGbNC95NAXxjFncwW4",
      de: "1qcxabOgY2QgErY1X5lRZL5VyGRJpMCLTVbCY0HV83oc"
    })
  ],

  [Camur.node_type.ntype_p4m]: [MANUAL_P_FAMILY],

  [Camur.node_type.ntype_p4mMesh]: [MANUAL_P_FAMILY],

  // The English sheet still sits in the "_Not yet online" folder in Drive and so is not in
  // the synced index; German is published. Until that changes only DE resolves.
  [Camur.node_type.ntype_fv03]: [
    datasheet({
      en: "1_ZBuCn0fEB5_rQzV6Cz5Kn8AWZs1mIIVbWFDeZXVVaE",
      de: "1kdnAMN595JYLY_NaYb34LfxrBKd-A8BRhsmc1sMhsY4"
    })
  ]
};

const CONTROLLER_DOCUMENTS = [
  datasheet({
    en: "14X2tF0yhYj7xuzd7pbXNGFp1sh4Kx7Z2j2rPD1wM8e4",
    de: "13pmXwSb677GczmpO5o9AoCRtGrdyvRZeyuosOcjCO7o"
  })
];

/**
 * The groups to hand DocumentsWindow for a node, or an empty list when the node type has
 * no documents listed yet, which also hides the button.
 */
export function node_document_groups(capability, node_type_name) {
  const documents = NODE_DOCUMENTS[capability];
  if (documents == null)
    return [];

  return [{ label: node_type_name || "Documents", documents }];
}

/** The same, for a controller. */
export function controller_document_groups(label) {
  return [{ label: label || "Controller", documents: CONTROLLER_DOCUMENTS }];
}
